import React, { ReactNode, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import RightUpImage from "public/static/main/carousel/right-up.png";
import RightBottomImage from "public/static/main/carousel/right-bottom.png";
import { Button } from "@shared/ui/Button";
import Link from "next/link";
import { IProduct } from "@entities/Product/types";
import { useRouter } from "next/router";
import { routePaths } from "@shared/routes";
import { ProductLink } from "./ui/ProductLink";
import Styled from "./styled";

interface IProps {
  products: IProduct[];
  children: ReactNode;
}

export function ProductLayout(props: IProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    skipSnaps: false,
    draggable: false,
  });
  const router = useRouter();
  const [category, productSlug] = router.query.slug;

  const selectedProduct = useMemo(
    () => props.products.find((product) => product.slug === productSlug),
    [productSlug]
  );
  const [selectedProductId, setSelectedProductId] = useState(
    selectedProduct.id
  );

  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [props.products]);

  const selectProduct = async (id: number) => {
    const selected = props.products.find((product) => product.id === id);
    await router.push(
      `/main/products/${category}/${selected.slug}/`,
      undefined,
      {
        scroll: false,
      }
    );
    setSelectedProductId(id);
  };

  const selectSlide = (productId: number, slideId: number) => {
    selectProduct(productId);
    if (emblaApi) emblaApi.scrollTo(slideId);
  };

  useEffect(() => {
    setSelectedProductId(selectedProduct.id);
  }, [selectedProduct]);

  return (
    <Styled.View>
      <div>
        <Styled.Empbla>
          <Styled.EmpblaViewport ref={emblaRef}>
            <Styled.EmpblaContainer>
              {props.products.map((product) => (
                <Styled.Slide key={product.id}>
                  <Styled.BackgroundImage
                    quality={90}
                    priority
                    width="1080"
                    height="720"
                    src={product.banner.url}
                    alt={product.banner.title}
                  />
                  <Styled.Promo>
                    <Styled.SContainer>
                      <Styled.Title
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1 }}
                        tag="h2"
                        fontSize="4.8rem"
                        weight={700}
                        color="basic_white"
                      >
                        {product.banner.title}
                      </Styled.Title>
                      <Styled.Subtitle
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        fontSize="2rem"
                        tag="div"
                        color="basic_white"
                        dangerouslySetInnerHTML={{
                          __html: product.banner.content,
                        }}
                      />
                      <Link
                        href={{
                          pathname: routePaths.main.products.request,
                          query: { productId: product.id },
                        }}
                      >
                        <Button
                          initial={{ opacity: 0, x: -50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 1, delay: 0.6 }}
                        >
                          {product.banner.button_text}
                        </Button>
                      </Link>
                    </Styled.SContainer>
                  </Styled.Promo>
                </Styled.Slide>
              ))}
            </Styled.EmpblaContainer>
            <Styled.RightImageContainer>
              <Styled.RightImageWrapper
                initial={{ opacity: 0.8, y: -150 }}
                animate={{ opacity: 0.8, y: 0 }}
                transition={{ duration: 1 }}
              >
                <Styled.RightImage src={RightUpImage} alt="logo-top" />
              </Styled.RightImageWrapper>
              <Styled.RightImageWrapper
                initial={{ opacity: 0.8, y: 150 }}
                animate={{ opacity: 0.8, y: 0 }}
                transition={{ duration: 1 }}
              >
                <Styled.RightImage src={RightBottomImage} alt="logo-bottom" />
              </Styled.RightImageWrapper>
            </Styled.RightImageContainer>
          </Styled.EmpblaViewport>
        </Styled.Empbla>
      </div>
      {props.products.length > 1 && (
        <Styled.NavButtons>
          {props.products.map((product, index) => (
            <ProductLink
              key={product.id}
              title={product.name}
              selected={selectedProductId === product.id}
              setSelected={() => selectSlide(product.id, index)}
            />
          ))}
        </Styled.NavButtons>
      )}
      <Styled.Children>{props.children}</Styled.Children>
    </Styled.View>
  );
}
